import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { HeaderService } from "@app/components/header/header.service";
import { formatUsPhone, phoneValidatorForCountry } from "@app/util/phone";
import {
    SsoHomeownerApiService,
    SsoProfileRequest,
} from "@app/services/sso/sso-homeowner-api.service";
import { AuthTokenService } from "@app/services/auth-token.service";
import { NavigationService } from "@app/services/navigation.service";
import { CountryCode } from "@app/models/CountryCode";
import { CountryCallingCodeApi } from "@app/services/apiServices/CountryCallingCodeApi";
import { take } from "rxjs/operators";

const REDIRECT_DELAY_MS = 1500;

@Component({
    selector: "app-create-sso-profile",
    template: require("./create-sso-profile.component.html"),
})
export class CreateSsoProfileComponent implements OnInit {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private auth = inject(AuthTokenService);
    private headerService = inject(HeaderService);
    private api = inject(SsoHomeownerApiService);
    private countryCallingCodeApi = inject(CountryCallingCodeApi);
    private navigationService = inject(NavigationService);

    created = false;
    failed = false;
    submitting = false;

    countryCodes: CountryCode[] = [];

    form: FormGroup = this.fb.group({
        firstName: ["", [Validators.required, Validators.maxLength(25)]],
        lastName: ["", [Validators.required, Validators.maxLength(35)]],
        countryCode: [null as CountryCode | null],
        phone: ["", [Validators.required, phoneValidatorForCountry]],
        email: [""],
    });

    ngOnInit(): void {
        this.headerService.setDisplayRightContent(false);
        const identity = this.auth.getSsoIdentity();
        this.form.patchValue({
            firstName: identity.firstName ?? "",
            lastName: identity.lastName ?? "",
            phone: identity.phone ?? "",
            email: identity.email,
        });

        this.form.get("email")!.disable();

        this.countryCallingCodeApi
            .getAllCountries(true)
            .pipe(take(1))
            .subscribe(({ data }) => {
                this.countryCodes = data;
                const defaultCountry =
                    data.find(c => c.country === "USA") ?? data[0] ?? null;
                this.form.get("countryCode")!.setValue(defaultCountry);
                this.reformatPhone();
                this.form.get("phone")!.updateValueAndValidity();
            });
    }

    showError(field: string): boolean {
        const control = this.form.get(field);
        return !!control && control.invalid && control.touched;
    }

    // Re-format the phone whenever the user types or the field loses focus.
    onPhoneInput(): void {
        this.reformatPhone();
    }

    onCountryChange(): void {
        this.reformatPhone();
        this.form.get("phone")!.updateValueAndValidity();
    }

    private reformatPhone(): void {
        const country: CountryCode | null = this.form.get('countryCode')!.value;
        const control = this.form.get('phone')!;
        const digits = (control.value ?? '').toString().replace(/[^0-9]/g, '');
        let formatted: string;
        if (country?.country === 'USA' || country?.country === 'CAN') {
            const national = digits[0] === '0' || digits[0] === '1' ? digits.slice(1) : digits;
            formatted = formatUsPhone(national.slice(0, 10));
        } else {
            const dialLength = (country?.value ?? '').length;
            formatted = digits.substring(0, 15 - dialLength);
        }
        if (formatted !== control.value) {
            control.setValue(formatted, { emitEvent: false });
        }
    }

    submit(): void {
        if (this.form.invalid || this.submitting) {
            this.form.markAllAsTouched();
            return;
        }
        const { firstName, lastName, phone, email, countryCode } = this.form.getRawValue();
        const userId = Number(this.auth.getSsoIdentity().userId);
        const country = (countryCode as CountryCode | null)?.country ?? "USA";
        const profile: SsoProfileRequest = {
            userId,
            firstName,
            lastName,
            phone: { type: "home", countryCallingCode: country, value: phone },
            email,
        };

        // Disable the button and show the banner on Create Profile click
        this.failed = false;
        this.submitting = true;

        // Create the profile on the backend; on success show the confirmation
        // banner, then sign the user into the registered app.
        this.api.updateProfile(profile).subscribe({
            next: () => {
                this.created = true;
                window.setTimeout(
                    () => this.redirectToProperties(),
                    REDIRECT_DELAY_MS
                );
            },
            error: () => {
                this.failed = true;
                this.submitting = false;
            },
        });
    }

    private redirectToProperties(): void {
        this.navigationService.navigateToProperties();
    }

    goBack(): void {
        if (this.submitting) {
            return;
        }
        this.router.navigate(["/sso/make-decision"]);
    }
}
